// Translate app shell: routing between the Translate page, Settings and Install,
// plus redirects from the old URLs (voice/text pages and group sessions, which
// now live in the Room app at /room/).

import { router } from '../utils/router.js';
import { boot, applyColorMode, registerServiceWorker } from '../shared/boot.js';
import { languageName } from '../shared/languages.js';

const pages = () => document.querySelectorAll('.page-section');

const PAGE_ROUTES = {
    'page-translate': '/',
    'page-main-menu': '/',
    'page-voice-translation': '/',
    'page-text-translation': '/',
    'page-settings': '/settings',
    'page-install': '/install',
};

const ROOM_PAGES = new Set(['page-session-selector', 'page-start-session', 'page-join-session', 'page-group-session']);

let currentPage = null;

/** Kept for Settings, which calls applyTheme(theme, mode). Only the mode matters now. */
function applyTheme(_theme, mode) {
    if (mode) localStorage.setItem('polyglot-mode', mode);
    applyColorMode();
}

async function showPageContent(pageId, params = {}) {
    pages().forEach((page) => page.classList.toggle('active', page.id === pageId));
    currentPage = pageId;
    window.dispatchEvent(new CustomEvent('pg:pagechange', { detail: { pageId } }));

    const initName = `init${pageId.replace('page-', '').replace(/-/g, '')}`;
    if (typeof window[initName] === 'function') {
        window[initName](params);
    }
}

function showPage(pageId) {
    if (ROOM_PAGES.has(pageId)) {
        window.location.assign('/room/');
        return;
    }
    return router.navigate(PAGE_ROUTES[pageId] || '/');
}

function setupRoutes() {
    router.addRoute('/', {
        name: 'translate',
        pageId: 'page-translate',
        onEnter: (match) => showPageContent('page-translate', match.params),
    });
    router.addRoute('/settings', {
        name: 'settings',
        pageId: 'page-settings',
        onEnter: (match) => showPageContent('page-settings', match.params),
    });
    router.addRoute('/install', {
        name: 'install',
        pageId: 'page-install',
        onEnter: (match) => showPageContent('page-install', match.params),
    });

    // Old pages: voice and text are now the one Translate page.
    for (const path of ['/voice-translation', '/text-translation']) {
        router.addRoute(path, {
            name: path.slice(1),
            pageId: 'page-translate',
            onEnter: () => router.navigate('/', { replace: true }),
        });
    }
    // Group sessions moved to the Room app.
    for (const path of ['/session-selector', '/start-session', '/join-session', '/group-session']) {
        router.addRoute(path, {
            name: path.slice(1),
            pageId: 'page-translate',
            onEnter: () => window.location.replace('/room/'),
        });
    }

    router.onNotFound(() => router.navigate('/', { replace: true }));
}

/**
 * Old links: /?join=CODE (shared room links and QR codes) and PWA shortcuts
 * (/?mode=voice|text|join|install). Returns true if it navigated away.
 */
function handleLegacyParams() {
    const params = new URLSearchParams(window.location.search);
    const join = params.get('join');
    if (join) {
        window.location.replace(`/room/${encodeURIComponent(join)}`);
        return true;
    }
    const mode = params.get('mode');
    if (mode === 'join') {
        window.location.replace('/room/');
        return true;
    }
    if (mode) {
        params.delete('mode');
        const query = params.toString();
        const path = mode === 'install' ? '/install' : '/';
        window.history.replaceState({}, document.title, `${path}${query ? `?${query}` : ''}`);
    }
    return false;
}

async function start() {
    applyColorMode();
    if (handleLegacyParams()) return;

    setupRoutes();
    const bootScreen = document.getElementById('pg-boot');
    try {
        await boot(bootScreen);
    } catch (error) {
        console.error('Startup failed', error);
    }
    bootScreen.hidden = true;
    await router.init();
}

window.showPage = showPage;
window.applyTheme = applyTheme;
window.getLanguageName = languageName;
window.router = router;
window.polyglot = { showPage, applyTheme, router, currentPage: () => currentPage };

registerServiceWorker();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
} else {
    start();
}
